/**
 * Reconnection retry utilities — exponential backoff with jitter.
 *
 * Used by Terminal.tsx to bound reconnect attempts after the Kali
 * controller restarts or the WebSocket drops.
 */

export interface RetryConfig {
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 5,
  baseDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2,
};

/**
 * Compute the retry delay for a given attempt using exponential backoff
 * with +/- 25% random jitter.
 *
 * @param attempt - 1-indexed attempt number (1 = first retry)
 * @param config - retry configuration
 * @param random - random value in [0, 1) for deterministic testing (defaults to Math.random())
 * @returns delay in milliseconds, clamped to [baseDelayMs, maxDelayMs]
 */
export function computeRetryDelay(
  attempt: number,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
  random: number = Math.random()
): number {
  const exponentialDelay = Math.min(
    config.baseDelayMs * Math.pow(config.backoffMultiplier, attempt - 1),
    config.maxDelayMs
  );
  // Jitter: +/- 25% of the computed delay
  const jitter = exponentialDelay * 0.25 * (random * 2 - 1);
  return Math.round(Math.max(config.baseDelayMs, exponentialDelay + jitter));
}

/**
 * Check whether a retry should be attempted.
 *
 * @param retryCount - number of retries already attempted
 * @param maxRetriesReached - whether max retries was flagged in a prior cycle
 * @param config - retry configuration
 * @returns true if another retry is allowed
 */
export function shouldRetry(
  retryCount: number,
  maxRetriesReached: boolean,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): boolean {
  return retryCount < config.maxRetries && !maxRetriesReached;
}

/**
 * Check whether a retry cycle is currently in progress.
 *
 * A cycle is "in progress" if either the counter has advanced past 0
 * (at least one retry attempted) or a backoff timer is pending.
 * Used by the Terminal auto-connect effect to suppress re-entry that
 * would reset the counter mid-cycle (the #869 reconnect storm).
 *
 * @param retryCount - current retry attempt count
 * @param timeoutHandle - current setTimeout handle (null if none pending)
 */
export function isRetryInProgress(retryCount: number, timeoutHandle: unknown): boolean {
  return retryCount > 0 || timeoutHandle !== null;
}
